import * as React from "react";
import { useFormik } from "formik";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import CssBaseline from "@material-ui/core/CssBaseline";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import LOGO from "src/assets/svgs/logo.png";

import { resetAccountantPassword } from "src/apiService/modules/accountant";
import PasswordField from "src/components/PasswordField";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: { width: "100%", marginTop: theme.spacing(1) },
  submit: { margin: theme.spacing(3, 0, 2), padding: 12, position: "relative" },
  loading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: "-12px",
    marginLeft: "-12px",
  },
  back: { marginTop: theme.spacing(2), textAlign: "center" },
}));

export const AccountantResetPasswordPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const resetToken = new URLSearchParams(location.search).get("token") || "";
  const [done, setDone] = React.useState(false);

  const formik = useFormik({
    initialValues: { password: "", confirm: "" },
    validate: (values) => {
      const errors: { password?: string; confirm?: string } = {};
      if (values.password.length < 8)
        errors.password = "Use at least 8 characters.";
      if (values.confirm !== values.password)
        errors.confirm = "Passwords don't match.";
      return errors;
    },
    onSubmit: async (values, { setStatus }) => {
      try {
        await resetAccountantPassword(resetToken, values.password);
        setDone(true);
      } catch (e) {
        setStatus({ error: (e as Error).message || "System error" });
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Box className="mb-5 h-36">
          <img src={LOGO} alt="My Reseller Genie" className="h-full" />
        </Box>
        <Typography component="h1" variant="h5">
          Choose a new password
        </Typography>
        {!resetToken ? (
          <Alert severity="error" style={{ marginTop: 16, width: "100%" }}>
            This reset link is missing or invalid. Please request a new one.
          </Alert>
        ) : done ? (
          <Alert severity="success" style={{ marginTop: 16, width: "100%" }}>
            Your password has been updated. You can now sign in.
          </Alert>
        ) : (
          <form
            className={classes.form}
            noValidate
            onSubmit={formik.handleSubmit}
          >
            <PasswordField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="New password"
              autoFocus
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              error={formik.touched.password && !!formik.errors.password}
              helperText={
                formik.touched.password ? formik.errors.password : undefined
              }
              disabled={formik.isSubmitting}
            />
            <PasswordField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirm"
              label="Confirm new password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirm}
              error={formik.touched.confirm && !!formik.errors.confirm}
              helperText={
                formik.touched.confirm ? formik.errors.confirm : undefined
              }
              disabled={formik.isSubmitting}
            />
            {formik.status?.error && !formik.isSubmitting && (
              <Alert severity="error">{formik.status.error}</Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={formik.isSubmitting || !formik.isValid}
            >
              Update password
              {formik.isSubmitting && (
                <CircularProgress
                  className={classes.loading}
                  size={24}
                  color="inherit"
                />
              )}
            </Button>
          </form>
        )}
        <div className={classes.back}>
          <Link component={RouterLink} to="/accountant/login" variant="body2">
            Back to sign in
          </Link>
        </div>
      </div>
    </Container>
  );
};

export default AccountantResetPasswordPage;
